import * as React from 'react';

import type { FilterOption } from '@/components/customTable/types';

import { useRef, useState } from 'react';

import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import apiClient from '@/api-client';
import { Box, CircularProgress } from '@mui/material';
import { CaretDown } from '@phosphor-icons/react';

/**
 * What to say when the list could not be fetched.
 *
 * A 403 is named as a permission, not softened into "something went wrong": it is the one
 * failure the user can act on, by asking whoever administers their account.
 */
function messageFor(error: any): string {
  const status = error?.response?.status;

  if (status === 403) return "You don't have permission to see this list";
  if (status === 401) return 'Your session has expired — sign in again';

  const detail = error?.response?.data?.message;
  return (Array.isArray(detail) ? detail[0] : detail) || 'Could not load these options';
}

export function FilterFetchSelect({
  label,
  showInputLabel,
  fixInputLabel = true,
  errorText,
  value,
  required,
  placeholder,
  onChange,
  disabled,
  getOptionsUrl,
  fetchLabel,
  fetchLabel2,
  fetchValue,
  disabledOptions,
  withoutFetchPagination,
}: {
  label: string;
  hideTextLabel?: boolean;
  showInputLabel?: boolean;
  fixInputLabel?: boolean;
  errorText?: string;
  value: FilterOption;
  required?: boolean;
  placeholder: string;
  onChange: (value: FilterOption | string) => void;
  disabled?: boolean;
  getOptionsUrl: string;
  fetchLabel: string;
  fetchLabel2?: string;
  fetchValue: string;
  disabledOptions?: string[];
  withoutFetchPagination?: boolean;
}) {
  const [options, setOptions] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  /**
   * Why the list is empty, when it is empty because the request failed.
   *
   * A picker that swallows its own error is indistinguishable from a picker whose answer is
   * genuinely "nothing" — a 403 on the endpoint behind it reads to the user as "you have no
   * agents", which is a different and much worse claim than "we could not ask".
   */
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const fetchOptions = async (searchTerm: string, pageNum: number, append = false) => {
    try {
      setLoading(true);
      setFetchError(null);
      setIsMounted(true);
      setPage(pageNum);
      const limit = 10;
      const offset = (pageNum - 1) * limit;
      const url = getOptionsUrl.includes('?')
        ? `${getOptionsUrl}&search=${searchTerm}&limit=${limit}&offset=${offset}`
        : `${getOptionsUrl}?search=${searchTerm}&limit=${limit}&offset=${offset}`;

      const res = await apiClient.get(url);
      const data = res.data.data || res.data;

      const newOptions = data.map((option: any) => ({
        value: option[fetchValue] || option.value,
        label: fetchLabel2
          ? `${option[fetchLabel]} ${option[fetchLabel2]}`
          : option[fetchLabel] || option.label,
      }));

      if (append) {
        setOptions((prev) => [...prev, ...newOptions].filter(option => option?.label?.toLowerCase().includes(searchTerm?.toLowerCase())));
      } else {
        setOptions(newOptions.filter(option => option?.label?.toLowerCase().includes(searchTerm?.toLowerCase())));
      }

      setHasMore(!withoutFetchPagination && newOptions.length > 0);
    } catch (error: any) {
      // Appending is the scroll-to-load path: keep what is already on screen and say the next
      // page failed, rather than emptying a list the user is part-way down.
      if (!append) {
        setOptions([]);
        // Let the next open retry instead of keeping a transient failure.
        setIsMounted(false);
      }
      setHasMore(false);
      setFetchError(messageFor(error));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event: any, newValue: string, reason: string) => {
    if (reason !== 'selectOption' && reason !== 'reset' && reason !== 'blur') {
      setSearch(newValue);

      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      
      setOptions([]);
      setLoading(true);
      
      debounceTimeout.current = setTimeout(() => {
        fetchOptions(newValue, 1, false);
      }, 300);
    }
  };

  function handleChange(event: unknown, newValue: FilterOption | null) {
    if (!newValue) {
      onChange('')
      return
    }
    onChange(newValue)
  }

  const handleOpen = () => {
    setIsOpen(true);

    if (!isMounted) {
      fetchOptions('', 1, false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const selectedOption = options.find((option) => String(option.value) === String(value.value));

  return (
    <FormControl fullWidth key={label}>
      <Autocomplete
        options={options}
        getOptionLabel={(option) => option.label}
        getOptionDisabled={(option) => disabledOptions?.includes(option.value) || false}
        isOptionEqualToValue={(option, optionValue) =>
          String(option.value) === String(optionValue.value)
        }
        value={selectedOption || null}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onOpen={handleOpen}
        onClose={handleClose}
        inputValue={isOpen ? search : selectedOption ? selectedOption.label : value.label || ''}
        popupIcon={<CaretDown size={18} />}   
        disabled={disabled}
        loading={loading}
        filterOptions={(x) => x}
        noOptionsText={fetchError ?? 'No options'}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={isOpen ? 'Search...' : placeholder}
            error={!!errorText || !!fetchError}
            helperText={fetchError ?? undefined}
            required={required}
            slotProps={
              fixInputLabel
                ? {
                    inputLabel: {
                      shrink: true,
                    },
                  }
                : {}
            }
            label={showInputLabel ? label : ''}
            // InputProps={{
            //   ...params.InputProps,
            //   endAdornment: (
            //     <>
            //       {loading ? <CircularProgress color="inherit" size={20} /> : null}
            //       {params.InputProps.endAdornment}
            //     </>
            //   ),
            // }}
          />
        )}
        ListboxProps={{
          role: 'list-box',
          style: { maxHeight: 220, fontSize: "16px" },
          onScroll: (event: React.UIEvent<HTMLUListElement>) => {
            const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
            const isNearBottom = scrollTop + clientHeight >= scrollHeight - 40;

            if (isNearBottom && hasMore && !loading) {
              fetchOptions(search, page + 1, true);
            }
          },
        }}
        slotProps={{
          paper: {
            sx: {
              fontSize: "16px !important",
            },
          },
        }}
        renderOption={(props, option, state) => {
          const isLast = state.index === options.length - 1;

          return (
            <Box
              component="li"
              {...props}
              key={option.value}
              sx={{
                mb: 0.5,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                ...(isLast && { flexDirection: 'column', alignItems: 'flex-start !important', mb: 0 }),
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" height="100%" flex={1}>
                <span style={{ flexGrow: 1, flexShrink: 1, minWidth: '60%' }}>{option.label}</span>
              </Box>
              {isLast && loading && hasMore && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: 1,
                    width: '100%',
                    marginTop: 1,
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              )}
            </Box>
          );
        }}
      />
    </FormControl>
  );
}
